<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { Pencil, RefreshCw } from '@lucide/vue'
import { Badge, Button, PageHeader, Section, Spinner, Table, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import PolicyEditModal from '../../components/billing/PolicyEditModal.vue'
import { useAdminAuth } from '../../composables/useAdminAuth.js'
import { formatInt } from '../llm/llm-format.js'

const api = inject('api')
if (!api) throw new Error('BillingPolicies: api provider is required')
const { t } = useI18n()
const auth = useAdminAuth()
const toast = useToast()
// 后端 PATCH 只放行 admin；operator 能看不能改，所以连编辑列都不给。
const canEdit = computed(() => auth.account.value.role === 'admin')

const policies = ref([])
const loading = ref(false)
const error = ref('')
const modal = reactive({ open: false, policy: null, saving: false, error: '' })

const columns = computed(() => {
  const list = [
    { key: 'action', label: t('visualGameAdmin.billing.policies.table.action'), width: '360px', wrap: true },
    { key: 'points', label: t('visualGameAdmin.billing.policies.table.points'), width: '120px', align: 'right', mono: true },
    { key: 'period_kind', label: t('visualGameAdmin.billing.policies.table.period'), width: '130px' },
    { key: 'timezone', label: t('visualGameAdmin.billing.policies.table.timezone'), width: '200px', mono: true },
    { key: 'enabled', label: t('visualGameAdmin.billing.policies.table.enabled'), width: '100px' },
  ]
  if (canEdit.value) list.push({ key: 'actions', label: t('visualGameAdmin.common.actions'), width: '110px', align: 'right' })
  return list
})

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    policies.value = (await api.commerce.listAdminActionPolicies()).items
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

function openEdit(policy) {
  modal.policy = policy
  modal.error = ''
  modal.open = true
}

async function savePolicy(body) {
  modal.saving = true
  modal.error = ''
  try {
    const saved = await api.commerce.updateAdminActionPolicy(modal.policy.action, body)
    policies.value = policies.value.map(item => (item.action === saved.action ? saved : item))
    modal.open = false
    toast.success(t('visualGameAdmin.billing.saved'))
  } catch (cause) {
    modal.error = cause.message
  } finally {
    modal.saving = false
  }
}
</script>

<template>
  <div class="vga-page" data-insp-path="apps/visual-game-admin/src/pages/billing/PoliciesPage.vue">
    <PageHeader :title="t('visualGameAdmin.billing.policies.title')">
      <template #actions>
        <Badge v-if="!canEdit" variant="neutral">{{ t('visualGameAdmin.billing.readOnly') }}</Badge>
        <Button variant="secondary" size="sm" :loading="loading" @click="load">
          <RefreshCw :size="14" />{{ t('visualGameAdmin.common.refresh') }}
        </Button>
      </template>
    </PageHeader>
    <p class="vga-page-description">{{ t('visualGameAdmin.billing.policies.description') }}</p>

    <Section :padded="false">
      <div v-if="loading && policies.length === 0" class="vga-state"><Spinner /></div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <Table v-else row-key="action" :columns="columns" :rows="policies">
        <template #action="{ row }">
          <div class="policy-action">
            <strong>{{ t(`visualGameAdmin.billing.actionNames.${row.action}`) }}</strong>
            <span class="vga-muted">{{ t(`visualGameAdmin.billing.actionHints.${row.action}`) }}</span>
          </div>
        </template>
        <template #points="{ row }">{{ formatInt(row.points) }}</template>
        <template #period_kind="{ row }">{{ t(`visualGameAdmin.billing.periods.${row.period_kind}`) }}</template>
        <template #timezone="{ row }">{{ row.timezone === null ? '—' : row.timezone }}</template>
        <template #enabled="{ row }">
          <Badge :variant="row.enabled ? 'success' : 'neutral'">
            {{ t(`visualGameAdmin.billing.enabledState.${row.enabled ? 'on' : 'off'}`) }}
          </Badge>
        </template>
        <template #actions="{ row }">
          <Button variant="ghost" size="sm" @click="openEdit(row)">
            <Pencil :size="14" />{{ t('visualGameAdmin.common.edit') }}
          </Button>
        </template>
      </Table>
    </Section>

    <PolicyEditModal
      v-model:open="modal.open"
      :policy="modal.policy"
      :saving="modal.saving"
      :error="modal.error"
      @submit="savePolicy"
    />
  </div>
</template>

<style scoped>
.policy-action {
  display: grid;
  gap: var(--drama-space-xs);
  padding: var(--drama-space-xs) 0;
}

.policy-action span {
  font-size: var(--drama-font-size-xs);
}
</style>
